import { Operation } from '@/shared/types/projectData';
import { OperationGroup, OperationGrouper } from './OperationGrouper';
import { UndoHandlers } from './UndoHandlers';
import { authService } from '@/features/auth/services/authService';

export interface UndoStackEntry {
  id: string;
  group: OperationGroup;
  timestamp: number;
  userId?: string;
  canUndo: boolean;
  canRedo: boolean;
  isSnapshot?: boolean; // For major state changes like imports
}

export interface StackState {
  position: number;
  entries: UndoStackEntry[];
  hasChanges: boolean;
  canUndo: boolean;
  canRedo: boolean;
}

/**
 * Hierarchical Undo/Redo Stack implementation
 * - 100-item limit
 * - Group-based undo/redo operations
 * - Jump-to-state functionality
 * - Stack change notifications
 * - User-specific undo stacks for collaboration
 */
export class UndoStack {
  private readonly MAX_STACK_SIZE = 100;
  private entries: UndoStackEntry[] = [];
  private currentPosition = -1; // -1 means no operations yet
  private grouper: OperationGrouper;
  private undoHandlers: UndoHandlers | null = null;
  private listeners: Set<(state: StackState) => void> = new Set();
  private isInBatch = false;
  private batchEntries: UndoStackEntry[] = [];
  private isExecutingOperation = false; // Prevent recursive operations
  private pendingEntries: UndoStackEntry[] = [];
  private historyGeneration = 0;
  private replayFence: Promise<void> | null = null;
  private resolveReplayFence: (() => void) | null = null;

  constructor() {
    this.grouper = new OperationGrouper();
  }

  /**
   * Set the undo handlers (called from ProjectDataContext)
   */
  public setUndoHandlers(handlers: UndoHandlers): void {
    this.undoHandlers = handlers;
  }

  /**
   * Ordinary mutations must await this fence before reading current state or
   * capturing history, then recheck their session/view identity. Replay operations
   * marked skipHistory must bypass it. Clear invalidates history, not active work.
   */
  public waitForReplay(): Promise<void> | null {
    return this.replayFence;
  }

  private beginReplay(): void {
    this.isExecutingOperation = true;
    this.replayFence = new Promise<void>(resolve => {
      this.resolveReplayFence = resolve;
    });
  }

  /**
   * Add operation to the stack with intelligent grouping
   */
  public addOperation(operation: Operation): UndoStackEntry {
    if ('skipHistory' in operation && operation.skipHistory) {
      // Return a dummy entry for consistency
      return {
        id: 'executing',
        group: {
          id: 'executing',
          name: 'Executing',
          operations: [operation],
          startTime: Date.now(),
          endTime: Date.now(),
          category: 'compound',
          canMerge: false
        },
        timestamp: Date.now(),
        canUndo: false,
        canRedo: false
      };
    }

    // Process operation through grouper for intelligent merging
    const group = this.grouper.processOperation(operation);
    
    // Check if this merged with existing active group
    const activeGroup = this.grouper.getActiveGroup();
    if (!this.isInBatch && activeGroup && activeGroup === group) {
      // Update the existing entry with the merged group
      const lastEntry = this.isExecutingOperation
        ? this.pendingEntries[this.pendingEntries.length - 1]
        : this.entries[this.currentPosition];
      if (lastEntry && lastEntry.group.id === group.id) {
        lastEntry.group = group;
        lastEntry.timestamp = Date.now();
        this.notifyListeners();
        return lastEntry;
      }
    }

    // Create new stack entry
    const entryId = `undo-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
    const entry: UndoStackEntry = {
      id: entryId,
      group: this.isInBatch
        ? {
            ...group,
            id: `batch-item-${entryId}`,
            operations: [structuredClone(operation)],
            metadata: group.metadata ? { ...group.metadata, affectedCount: 1 } : undefined,
          }
        : group,
      timestamp: Date.now(),
      userId: authService.getUser()?.id,
      canUndo: true,
      canRedo: false
    };

    // Handle batch operations
    if (this.isInBatch) {
      this.batchEntries.push(entry);
      return entry;
    }

    this.pushEntry(entry);
    return entry;
  }

  /**
   * Start batch operation (for complex multi-step operations)
   */
  public startBatch(): void {
    // A batch is a hard history boundary: it must never merge into the last
    // committed group, nor retain that group's live identity.
    this.grouper.forceCloseGroup();
    this.isInBatch = true;
    this.batchEntries = [];
  }

  /**
   * End batch operation and create snapshot
   */
  public endBatch(batchName?: string): UndoStackEntry | null {
    if (!this.isInBatch || this.batchEntries.length === 0) {
      this.isInBatch = false;
      this.batchEntries = [];
      this.grouper.forceCloseGroup();
      return null;
    }

    // Create compound group for batch
    const batchGroup: OperationGroup = {
      id: `batch-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
      name: batchName || `Batch operation (${this.batchEntries.length} operations)`,
      operations: this.batchEntries.flatMap(entry => entry.group.operations),
      startTime: this.batchEntries[0].timestamp,
      endTime: Date.now(),
      category: 'compound',
      canMerge: false,
      metadata: {
        affectedCount: this.batchEntries.length
      }
    };

    const batchEntry: UndoStackEntry = {
      id: `batch-undo-${Date.now()}`,
      group: batchGroup,
      timestamp: Date.now(),
      userId: authService.getUser()?.id,
      canUndo: true,
      canRedo: false,
      isSnapshot: true
    };

    this.isInBatch = false;
    this.batchEntries = [];
    this.grouper.forceCloseGroup();
    this.pushEntry(batchEntry);
    return batchEntry;
  }

  /**
   * Discard an in-progress batch without changing committed history.
   */
  public cancelBatch(): void {
    this.isInBatch = false;
    this.batchEntries = [];
    this.grouper.forceCloseGroup();
  }

  /**
   * Push entry to stack with size management
   */
  private pushEntry(entry: UndoStackEntry): void {
    if (this.isExecutingOperation) {
      this.pendingEntries.push(entry);
      if (this.pendingEntries.length > this.MAX_STACK_SIZE) {
        this.pendingEntries.splice(0, this.pendingEntries.length - this.MAX_STACK_SIZE);
      }
      return;
    }
    this.commitEntry(entry);
    this.notifyListeners();
  }

  private commitEntry(entry: UndoStackEntry): void {
    // Remove any redo entries (we're creating a new branch)
    if (this.currentPosition < this.entries.length - 1) {
      this.entries = this.entries.slice(0, this.currentPosition + 1);
    }

    // Add new entry
    this.entries.push(entry);
    this.currentPosition = this.entries.length - 1;

    // Enforce size limit (keep most recent operations)
    if (this.entries.length > this.MAX_STACK_SIZE) {
      const removeCount = this.entries.length - this.MAX_STACK_SIZE;
      this.entries = this.entries.slice(removeCount);
      this.currentPosition -= removeCount;
    }
  }

  private finishReplay(): void {
    const resolveFence = this.resolveReplayFence;
    for (const entry of this.pendingEntries) this.commitEntry(entry);
    this.pendingEntries = [];
    this.isExecutingOperation = false;
    this.replayFence = null;
    this.resolveReplayFence = null;
    this.notifyListeners();
    // A listener can start another replay. Resolve only this replay's fence;
    // admission callers must recheck waitForReplay after each awaited fence.
    resolveFence?.();
  }

  /**
   * Undo last operation group
   */
  public async undo(): Promise<UndoStackEntry | null> {
    if (!this.canUndo() || this.isExecutingOperation || !this.undoHandlers) {
      return null;
    }

    this.beginReplay();
    const generation = this.historyGeneration;
    const handlers = this.undoHandlers;
    const entry = this.entries[this.currentPosition];
    let lastCompletedIndex = entry.group.operations.length;
    
    try {
      // Force close any active grouping
      this.grouper.forceCloseGroup();
      
      for (let i = entry.group.operations.length - 1; i >= 0; i--) {
        const operation = entry.group.operations[i];
        await handlers.executeUndo(operation);
        if (generation !== this.historyGeneration) return null;
        lastCompletedIndex = i;
      }
      
      if (this.entries.includes(entry)) this.currentPosition--;
      
    } catch (error) {
      if (generation !== this.historyGeneration) throw error;
      for (let i = lastCompletedIndex; i < entry.group.operations.length; i++) {
        try {
          await handlers.executeRedo(entry.group.operations[i]);
          if (generation !== this.historyGeneration) break;
        } catch {
          break;
        }
      }
      throw error;
    } finally {
      this.finishReplay();
    }
    
    return entry;
  }

  /**
   * Redo next operation group
   */
  public async redo(): Promise<UndoStackEntry | null> {
    if (!this.canRedo() || this.isExecutingOperation || !this.undoHandlers) {
      return null;
    }

    this.beginReplay();
    const generation = this.historyGeneration;
    const handlers = this.undoHandlers;
    this.currentPosition++;
    const entry = this.entries[this.currentPosition];
    let lastCompletedIndex = -1;
    
    try {
      this.grouper.forceCloseGroup();
      for (let i = 0; i < entry.group.operations.length; i++) {
        const operation = entry.group.operations[i];
        await handlers.executeRedo(operation);
        if (generation !== this.historyGeneration) return null;
        lastCompletedIndex = i;
      }
      
    } catch (error) {
      if (generation !== this.historyGeneration) throw error;
      for (let i = lastCompletedIndex; i >= 0; i--) {
        try {
          await handlers.executeUndo(entry.group.operations[i]);
          if (generation !== this.historyGeneration) break;
        } catch {
          break;
        }
      }
      if (generation === this.historyGeneration && this.entries.includes(entry)) this.currentPosition--;
      throw error;
    } finally {
      this.finishReplay();
    }
    
    return entry;
  }

  /**
   * Jump to specific position in undo history (like Blender's undo list)
   */
  public jumpToPosition(position: number): UndoStackEntry[] | null {
    if (this.isExecutingOperation) return null;
    if (position < -1 || position >= this.entries.length) return null;

    const oldPosition = this.currentPosition;
    this.currentPosition = position;

    // Force close any active grouping
    this.grouper.forceCloseGroup();

    // Return the operations that need to be applied/reverted
    const operations: UndoStackEntry[] = [];
    
    if (oldPosition < position) {
      // Redoing - return entries from oldPosition+1 to position
      for (let i = oldPosition + 1; i <= position; i++) {
        operations.push(this.entries[i]);
      }
    } else if (oldPosition > position) {
      // Undoing - return entries from oldPosition down to position+1 (in reverse)
      for (let i = oldPosition; i > position; i--) {
        operations.push(this.entries[i]);
      }
    }

    this.notifyListeners();
    return operations;
  }

  /**
   * Get current stack state
   */
  public getState(): StackState {
    return {
      position: this.currentPosition,
      entries: [...this.entries],
      hasChanges: this.currentPosition >= 0,
      canUndo: this.canUndo(),
      canRedo: this.canRedo()
    };
  }

  /**
   * Get undo list for UI (like Blender's undo history panel)
   */
  public getUndoList(): Array<{
    name: string;
    timestamp: number;
    category: string;
    isCurrentPosition: boolean;
    canJumpTo: boolean;
    metadata?: OperationGroup['metadata'];
  }> {
    return this.entries.map((entry, index) => ({
      name: entry.group.name,
      timestamp: entry.timestamp,
      category: entry.group.category,
      isCurrentPosition: index === this.currentPosition,
      canJumpTo: entry.canUndo,
      metadata: entry.group.metadata
    }));
  }

  /**
   * Get operations for specific range (for applying/reverting)
   */
  public getOperationsInRange(startPos: number, endPos: number): Operation[] {
    const operations: Operation[] = [];
    const start = Math.max(0, Math.min(startPos, endPos));
    const end = Math.min(this.entries.length - 1, Math.max(startPos, endPos));

    for (let i = start; i <= end; i++) {
      operations.push(...this.entries[i].group.operations);
    }

    return operations;
  }

  /**
   * Create snapshot for major operations (import, etc.)
   */
  public createSnapshot(name: string, operations?: Operation[]): UndoStackEntry {
    const snapshotGroup: OperationGroup = {
      id: `snapshot-${Date.now()}`,
      name,
      operations: operations || [],
      startTime: Date.now(),
      endTime: Date.now(),
      category: 'compound',
      canMerge: false,
      metadata: {
        affectedCount: operations?.length || 0
      }
    };

    const entry: UndoStackEntry = {
      id: `snapshot-undo-${Date.now()}`,
      group: snapshotGroup,
      timestamp: Date.now(),
      userId: authService.getUser()?.id,
      canUndo: true,
      canRedo: false,
      isSnapshot: true
    };

    this.pushEntry(entry);
    return entry;
  }

  /**
   * Clear all history (use carefully)
   */
  public clear(): void {
    this.historyGeneration++;
    this.pendingEntries = [];
    this.entries = [];
    this.currentPosition = -1;
    this.cancelBatch();
    this.notifyListeners();
  }

  /**
   * Remove a specific entry from the stack.
   * Intended for optimistic operations that fail before they become durable.
   */
  public removeEntry(entryId: string): boolean {
    const pendingIndex = this.pendingEntries.findIndex((entry) => entry.id === entryId);
    if (pendingIndex !== -1) {
      this.pendingEntries.splice(pendingIndex, 1);
      this.grouper.forceCloseGroup();
      return true;
    }
    const index = this.entries.findIndex((entry) => entry.id === entryId);
    if (index === -1) {
      return false;
    }

    this.entries.splice(index, 1);

    if (this.currentPosition >= index) {
      this.currentPosition = Math.max(-1, this.currentPosition - 1);
    }

    this.grouper.forceCloseGroup();
    this.notifyListeners();
    return true;
  }

  /**
   * Check if undo is possible
   */
  public canUndo(): boolean {
    return this.currentPosition >= 0 && this.entries[this.currentPosition]?.canUndo;
  }

  /**
   * Check if redo is possible
   */
  public canRedo(): boolean {
    return this.currentPosition < this.entries.length - 1;
  }

  /**
   * Get current operation count
   */
  public getOperationCount(): number {
    return this.entries.length;
  }

  /**
   * Get operations since specific position
   */
  public getOperationsSince(position: number): Operation[] {
    if (position < 0 || position >= this.entries.length) return [];
    
    const operations: Operation[] = [];
    for (let i = position; i <= this.currentPosition; i++) {
      operations.push(...this.entries[i].group.operations);
    }
    return operations;
  }

  /**
   * Subscribe to stack changes
   */
  public subscribe(listener: (state: StackState) => void): () => void {
    this.listeners.add(listener);
    
    // Return unsubscribe function
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Notify all listeners of state changes
   */
  private notifyListeners(): void {
    const state = this.getState();
    this.listeners.forEach(listener => {
      try {
        listener(state);
      } catch {
        // Listener errors are silently ignored to prevent cascading failures
      }
    });
  }

  /**
   * Get grouping statistics for debugging
   */
  public getGroupingStats() {
    return {
      stackSize: this.entries.length,
      currentPosition: this.currentPosition,
      canUndo: this.canUndo(),
      canRedo: this.canRedo(),
      activeGroup: this.grouper.getGroupingStats(),
      recentGroups: this.entries.slice(-5).map(e => ({
        name: e.group.name,
        category: e.group.category,
        operationCount: e.group.operations.length
      }))
    };
  }

  /**
   * Export stack state for persistence/debugging
   */
  public exportState() {
    return {
      entries: this.entries.map(entry => ({
        id: entry.id,
        groupName: entry.group.name,
        category: entry.group.category,
        operationCount: entry.group.operations.length,
        timestamp: entry.timestamp,
        userId: entry.userId,
        isSnapshot: entry.isSnapshot
      })),
      currentPosition: this.currentPosition,
      stats: this.getGroupingStats()
    };
  }
}

// Global instance for the application
export const undoStack = new UndoStack();
